// src/services/AccountDataService.ts
import axios from "axios";


let isBalanceSocketConnected = false;

export class AccountDataService {

  private baseUrl = import.meta.env.VITE_API_BASE_URL;

  private privateToken: string | null = null;

  private instanceEndpoint: string | null = null;

  private isDemoMode: boolean = false;

  private async getPrivateToken(): Promise<string> {
    if (this.privateToken && this.instanceEndpoint) {
      return `${this.instanceEndpoint}?token=${this.privateToken}`;
    }

    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) throw new Error("No access token found");

    const res = await axios.get(
      `${this.baseUrl}v0/kucoin/user/spot/orders/private-token`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "*/*",
        },
      }
    );

    if (!res.data?.token || !res?.data?.instanceServers?.length) {
      throw new Error("Invalid private token response");
    }

    const { token, instanceServers } = res.data;
    this.privateToken = token;
    this.instanceEndpoint = instanceServers[0].endpoint;

    // Detect demo mode: the backend returns 'wss://demo.localhost' for demo users
    if (this.instanceEndpoint?.includes('demo.localhost') || token === 'demo-private-token') {
      this.isDemoMode = true;
    }

    return `${this.instanceEndpoint}?token=${this.privateToken}`;
  }

  // ---------------- Open Orders WS (existing) ----------------
  async connectOpenOrdersWebSocket(onOrderUpdate: (order: any) => void): Promise<WebSocket> {
    const wsUrl = await this.getPrivateToken().catch(err => {
      console.error("Failed to get private token for open orders WS:", err);
      return "";
    });
    if (!wsUrl) return Promise.reject("No WS URL");

    // In demo mode, skip the real WebSocket connection
    if (this.isDemoMode) {
      console.log("🔐 [DEMO] Skipping KuCoin open orders WebSocket");
      return { close: () => {} } as unknown as WebSocket;
    }

    const ws = new WebSocket(wsUrl);
    let pingInterval: NodeJS.Timeout | null = null;

    ws.onopen = () => {
      // console.log("🔐 Connected to KuCoin open orders websocket");

      ws.send(
        JSON.stringify({
          id: Date.now(),
          type: "subscribe",
          topic: "/spotMarket/tradeOrdersV2",
          privateChannel: true,
          response: true,
        })
      );

      // Add ping interval here (only this part is new)
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ id: Date.now(), type: "ping" }));
        }
      }, 18000);
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === "message" && msg.topic === "/spotMarket/tradeOrdersV2") {
        onOrderUpdate(msg.data);
      }
    };

    ws.onclose = () => {
      console.log("❌ Open orders websocket closed");
      if (pingInterval) clearInterval(pingInterval);
    };

    ws.onerror = (err) => {
      console.error("⚠️ WebSocket error:", err);
    };

    return ws;
  }

  // ---------------- New: Account Balance WS ----------------
  async connectAccountBalanceWebSocket(onBalanceUpdate: (data: any) => void): Promise<WebSocket> {
    const wsUrl = await this.getPrivateToken();

    // In demo mode, skip the real WebSocket connection — balances come from REST
    if (this.isDemoMode) {
      isBalanceSocketConnected = true;
      console.log("🔐 [DEMO] Skipping KuCoin balance WebSocket — using REST balances");
      // Return a dummy WebSocket-like object that does nothing
      return { close: () => { isBalanceSocketConnected = false; } } as unknown as WebSocket;
    }

    const ws = new WebSocket(wsUrl);

    let pingInterval: NodeJS.Timeout | null = null;

    ws.onopen = () => {
      isBalanceSocketConnected = true;
      console.log("🔐 Connected to KuCoin account balance websocket");
      ws.send(
        JSON.stringify({
          id: Date.now(),
          type: "subscribe",
          topic: "/account/balance",
          privateChannel: true,
          response: true,
        })
      );

      pingInterval = setInterval(() => {
        ws.send(JSON.stringify({ id: Date.now(), type: "ping" }));
      }, 18000);
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      console.log(msg);
      if (msg.type === "message" && msg.topic === "/account/balance") {
        onBalanceUpdate(msg.data);
      }
    };

    ws.onclose = () => {
      isBalanceSocketConnected = false;
      console.log("❌ Account balance websocket closed");
      if (pingInterval) clearInterval(pingInterval);
    };

    ws.onerror = (err) => {
      isBalanceSocketConnected = false;
      console.error("⚠️ Account balance WS error:", err);
    };

    return ws;
  }

  public getIsBalanceSocketConnected(): boolean {
    return isBalanceSocketConnected;
  }
}
